<script setup lang="ts">
import { CheckCircleIcon } from '@heroicons/vue/solid';
import { ExclamationCircleIcon } from '@heroicons/vue/solid';
import { Loader2 } from 'lucide-vue-next';
import {useMustReadStore} from "~/stores/mustReadPages";
import type {MustReadPageDto} from "~/types/hdapi";

// TODO: Generate these with the openapi script, unable to run atm due to threatlocker.
interface LoginPageResponse {
  total: number;
  items: LoginPageItem[];
}

interface LoginPageItem {
  contentType: string;
  name: string;
  createDate: string;
  updateDate: string;
  route: Route;
  id: string;
  properties: LoginPageProperties;
  cultures: Record<string, never>;
}

interface Route {
  path: string;
  startItem: StartItem;
}

interface StartItem {
  id: string;
  path: string;
}

interface RichText {
  markup: string;
  blocks: any;
}

interface LoginPageProperties {
  primaryTitle: string;
  primaryCopy: RichText;
  secondaryTitle: string;
  secondaryCopy: RichText;
  secondaryBodyCopy: string[];
  callToAction: CallToAction[];
  secondaryCta: CallToAction[];
}

interface CallToAction {
  url: string | null;
  queryString: string | null;
  title: string;
  target: string | null;
  destinationId: string;
  destinationType: string;
  route: Route;
  linkType: string;
}

const { login } = useAuth();

definePageMeta({
  layout: 'unauthenticated',
  title: 'Login'
});

const { data, error } = await useFetch<LoginPageResponse>('/api/content/login', {});

if (error.value) {
  console.error('Request failed with error: ' + error.value.message);
}

const pageContent = data.value?.items.find(item => item.contentType === 'loginPage')?.properties;

const email = ref();
const password = ref();
const isLoading = ref(false);
const authenticationError = ref();

const handleSubmit = async () => {
  isLoading.value = true;
  authenticationError.value = false;

  const { success, message } = await login({ email: email.value, password: password.value });

  if (success) {
    const mustReadState = useState<MustReadPageDto[]>('must-read-pages', () => [])
    mustReadState.value = [];

    await navigateTo('/');
  } else {
    authenticationError.value = message;
  }

  isLoading.value = false;
};

const loginButtonText = computed(() => pageContent?.callToAction[0]?.title ?? 'Login');
const secondaryCtaText = computed(() => pageContent?.secondaryCta[0]?.title ?? 'Apply for a trade account');
</script>

<template>
  <div class="flex flex-1 flex-col md:flex-row">
    <div class="w-full bg-anti-flash-white py-8 text-stormcloud md:w-1/2 md:py-20 xl:py-28">
      <div class="h-fit pl-5 pr-5 md:ml-auto md:max-w-[620px] md:pr-12 lg:pr-24">
        <AuthenticationTitle :title="pageContent?.primaryTitle" :rich-text="pageContent?.primaryCopy?.markup" />
        <ClientOnly>
          <form @submit.prevent="handleSubmit" class="mt-6 flex flex-col space-y-5 md:pr-5">
            <div class="space-y-2">
              <Label for="email" class="text-base">Email</Label>
              <Input
                id="email"
                v-model="email"
                type="text"
                class="rounded-none border-gainsboro"
                :disabled="isLoading"
              />
            </div>
            <AuthenticationPasswordField
              v-model="password"
              show-forgot-password-link
              label="Password"
              id="password"
              :disabled="isLoading"
            />
            <span v-if="authenticationError" class="flex flex-row gap-2 text-sm text-cinnabar">
              <span class="pt-0.5"> <ExclamationCircleIcon class="size-4" /> </span
              ><span>{{ authenticationError }}</span>
            </span>
            <Button type="submit" class="relative w-full md:w-fit" :disabled="!email || !password">
              <Loader2 v-if="isLoading" class="absolute h-4 w-4 animate-spin" />
              <span :class="{ 'opacity-0': isLoading }">{{ loginButtonText }}</span></Button
            >
            <nuxt-link
              to="/forgot-password"
              tabindex="0"
              aria-label="Forgot password"
              class="block font-heading text-sm uppercase tracking-heading md:hidden"
              >Forgot password?</nuxt-link
            >
          </form>
        </ClientOnly>
      </div>
    </div>
    <div class="relative w-full bg-stormcloud py-8 text-white md:w-1/2 md:py-20 xl:py-28">
      <div class="pl-5 pr-5 md:mr-auto md:max-w-[620px] md:pl-12 md:pr-5 lg:pl-24">
        <AuthenticationTitle :title="pageContent?.secondaryTitle" :rich-text="pageContent?.secondaryCopy?.markup" />
        <ul class="mt-6 flex grid-cols-2 flex-col gap-5 lg:grid">
          <li v-for="item in pageContent?.secondaryBodyCopy" class="flex flex-row items-start gap-3 lg:max-w-52">
            <span class="pt-1">
              <CheckCircleIcon class="size-5 stroke-2" />
            </span>
            <span>{{ item }}</span>
          </li>
        </ul>
        <Button
          v-if="pageContent?.secondaryCta"
          as-child
          class="mt-6 w-full cursor-pointer md:w-fit"
          variant="secondary"
        >
          <nuxt-link :to="pageContent?.secondaryCta[0]?.route.path" :target="pageContent?.secondaryCta[0]?.target">
            {{ secondaryCtaText }}
          </nuxt-link></Button
        >
      </div>
    </div>
  </div>
</template>

<style scoped></style>
